import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  meetingDecisionTypeFormSchema,
  type MeetingDecisionTypeFormValues,
} from './meetingDecisionTypeFormSchema'
import type { MeetingDecisionType, MeetingDecisionTypeUpsert } from '../types'

interface Props {
  visible: boolean
  initial: MeetingDecisionType | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: MeetingDecisionTypeUpsert) => void
  onHide: () => void
}

/** Toplantı karar tipi ekle/düzenle formu (032: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function MeetingDecisionTypeFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(meetingDecisionTypeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: MeetingDecisionTypeFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={
        initial
          ? t('activity.meetingDecisionTypes.form.editTitle')
          : t('activity.meetingDecisionTypes.form.newTitle')
      }
      submitLabel={t('activity.meetingDecisionTypes.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.meetingDecisionTypes.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.meetingDecisionTypes.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
